import { useEffect, useMemo } from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { PRIVACY_POLICY_BY_LANG } from '@/constants/privacyPolicyContent';
import { COOKIE_POLICY_BY_LANG } from '@/constants/cookiePolicyContent';
import { WEBSITE_PRIVACY_NOTICE_BY_LANG } from '@/constants/websitePrivacyNoticeContent';
import { applyPageSeo, buildOrganizationJsonLd } from '@/utils/seo';

const NOINDEX_PRIVATE =
  String(process.env.REACT_APP_SEO_NOINDEX_PRIVATE || '')
    .trim()
    .toLowerCase() === 'true' ||
  String(process.env.REACT_APP_SEO_NOINDEX_PRIVATE || '').trim() === '1';

function routeMeta(page, lang) {
  const isTr = lang === 'tr';
  const defaults = {
    home: {
      title: isTr ? 'Toptan Türk ürünleri B2B platformu' : 'B2B wholesale Turkish products',
      description: isTr
        ? 'DeMaxtore ile ithalatı tek yapılandırılmış akışta yönetin: doğrulanmış Türk üreticileri, navlun karşılaştırması, muayene ve fabrikadan depoya sevkiyat takibi. Daha az karmaşa, daha az risk, daha fazla kontrol.'
        : 'DeMaxtore brings the entire import process into one structured workflow: verified Turkish manufacturers, freight comparison, inspections, and shipment tracking to your warehouse. Less complexity. Less risk. More control.',
      canonicalPath: '/',
    },
    products: {
      title: isTr ? 'Ürün kataloğu' : 'Product catalog',
      description: isTr
        ? 'Kategori ve alt kategorilere göre toptan ürünleri keşfedin.'
        : 'Browse wholesale products by category and subcategory.',
      canonicalPath: '/products',
    },
    categories: {
      title: isTr ? 'Ürün kataloğu' : 'Product catalog',
      description: isTr
        ? 'Kategori ve alt kategorilere göre toptan ürünleri keşfedin.'
        : 'Browse wholesale products by category and subcategory.',
      canonicalPath: '/products',
    },
    about: {
      title: isTr ? 'Hakkımızda' : 'About us',
      description: isTr
        ? 'DeMaxtore ve küresel B2B ticaret misyonumuz.'
        : 'Learn about DeMaxtore and our global B2B trade mission.',
      canonicalPath: '/about',
    },
    contact: {
      title: isTr ? 'İletişim' : 'Contact',
      description: isTr
        ? 'Satış ve iş birliği için bize ulaşın.'
        : 'Reach our team for sales and partnerships.',
      canonicalPath: '/contact',
    },
    blog: {
      title: isTr ? 'Blog' : 'Blog',
      description: isTr ? 'Pazar içgörüleri ve haberler.' : 'Market insights and trade news.',
      canonicalPath: '/blog',
    },
    rfq: {
      title: isTr ? 'Teklif sepeti (RFQ)' : 'RFQ basket',
      description: isTr ? 'Teklif talebinizi gönderin.' : 'Submit your request for quotation.',
      canonicalPath: '/rfq',
      noindex: NOINDEX_PRIVATE,
    },
    auth: {
      title: isTr ? 'Hesap' : 'Account',
      description: isTr ? 'Müşteri paneli girişi.' : 'Customer dashboard sign-in.',
      canonicalPath: '/account',
      noindex: true,
    },
    account_dashboard: {
      title: isTr ? 'Teklif taleplerim' : 'My RFQs',
      description: isTr ? 'Gönderdiğiniz RFQ özeti.' : 'Your submitted request for quotations.',
      canonicalPath: '/account/dashboard',
      noindex: true,
    },
    kvkk: {
      title: WEBSITE_PRIVACY_NOTICE_BY_LANG[lang]?.title || 'Website Privacy Notice',
      description:
        WEBSITE_PRIVACY_NOTICE_BY_LANG[lang]?.intro?.[1]?.text?.slice(0, 155) ||
        'How DeMaxtore collects and processes personal data through the website.',
      canonicalPath: '/kvkk',
    },
    gizlilik_politikasi: {
      title: PRIVACY_POLICY_BY_LANG[lang]?.title || 'Privacy Policy of DeMaxtore.com',
      description:
        PRIVACY_POLICY_BY_LANG[lang]?.subtitle?.slice(0, 155) ||
        'How DeMaxtore collects and processes personal and business data.',
      canonicalPath: '/gizlilik-politikasi',
    },
    cerez_politikasi: {
      title: COOKIE_POLICY_BY_LANG[lang]?.title || 'Cookie Policy',
      description:
        COOKIE_POLICY_BY_LANG[lang]?.intro?.[0]?.text?.slice(0, 155) ||
        'How DeMaxtore uses cookies and similar technologies.',
      canonicalPath: '/cerez-politikasi',
    },
  };
  return defaults[page] || defaults.home;
}

/**
 * Ürün detayı dışındaki sayfalarda title/description/canonical günceller.
 */
export default function RouteSeoSync({ currentPage, selectedProductId }) {
  const { lang } = useLanguage();

  const orgLd = useMemo(() => buildOrganizationJsonLd(), []);

  useEffect(() => {
    if (currentPage === 'product' && selectedProductId) {
      return;
    }
    const meta = routeMeta(currentPage, lang);
    const ogFromEnv = String(process.env.REACT_APP_SEO_DEFAULT_OG_IMAGE || '').trim();
    applyPageSeo({
      title: meta.title,
      description: meta.description,
      canonicalPath: meta.canonicalPath,
      ogImage: ogFromEnv || undefined,
      noindex: meta.noindex,
      jsonLd: orgLd,
      jsonLdId: 'demaxtore-jsonld',
    });
  }, [currentPage, selectedProductId, lang, orgLd]);

  return null;
}
